import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { ActionTypes } from 'state/actions/alert';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { Box } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { useParams } from 'react-router-dom';
import { Loader } from 'components/atoms/Loader';
import { ILicenceTemplateFormValues } from 'models/licenceConfigurator';
import { useConfigurationLicence } from '../hooks';
import { FormLicenceTemplatePage } from '../FormLicenceTemplatePage';
import { initialFormValues } from '../helper';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';

const EditLicenceTemplatePage = () => {
  const { clearAlert, samePageAlert } = useAlert();
  const { selectedLanguage } = useLang();
  const [isLoading, setIsLoading] = useState(true);
  const [formValues, setFormValues] = useState<ILicenceTemplateFormValues>();
  const { t } = useTranslations();
  const { id } = useParams<{ id: string }>();
  const { getLicence, licence, loadDictionaries, weaponLicenceGoodReason } =
    useConfigurationLicence();
  const { getFieldsOptional, requiredFieldsName } = useSystemFieldsConfiguration();

  useEffect(() => {
    getFieldsOptional();
  }, []);

  useEffect(() => {
    (async function getDataInit() {
      setIsLoading(true);
      if (id) {
        getLicence(id);
      }
      setIsLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (licence && weaponLicenceGoodReason?.length) {
      const goodReasonDictionaryInitial = weaponLicenceGoodReason.reduce(
        (acc, item) => {
          acc[item.uniqueId] = false;
          return acc;
        },
        {} as Record<string, boolean>,
      );

      const legalTypes = Object.keys(initialFormValues.owner.legalTypes).reduce(
        (acc, key) => {
          acc[key] = licence.owner.legalTypes.includes(key);
          return acc;
        },
        {} as Record<string, boolean>,
      );
      const goodReasonDictionaryIds = licence.conditions.goodReasonDictionaryIds
        ? Object.keys(goodReasonDictionaryInitial).reduce(
            (acc, key) => {
              acc[key] = licence.conditions.goodReasonDictionaryIds.includes(key);
              return acc;
            },
            {} as Record<string, boolean>,
          )
        : goodReasonDictionaryInitial;

      const { general, owner, conditions } = licence;

      type Individual = typeof owner.individual;
      const individual: Individual = { ...owner.individual };
      requiredFieldsName.forEach((field) => {
        if (field in individual) {
          (individual[field as keyof Individual] as boolean) = true;
        }
      });

      type Organisation = typeof owner.organisation;
      const organisation: Organisation = { ...owner.organisation };
      requiredFieldsName.forEach((field) => {
        if (field in organisation) {
          (organisation[field as keyof Organisation] as boolean) = true;
        }
      });

      setFormValues({
        conditions: {
          ...conditions,
          goodReasonDictionaryIds,
        },
        general: {
          ...general,
        },
        owner: {
          ...owner,
          individual,
          organisation,
          legalTypes,
        },
      });
    }
  }, [licence, weaponLicenceGoodReason]);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([
        EDictionaryTypes.weaponLicenceCategory,
        EDictionaryTypes.weaponLicenceCompetency,
        EDictionaryTypes.weaponLicenceGoodReason,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('module.administration.name'),
            route: ERouteLinks.Administration,
          },
          {
            label: t('licenceConfiguration.header'),
            route: ERouteLinks.LicenceConfigurationPage,
          },
          {
            label: t('licenceConfiguration.detail.header'),
            route: ERouteLinks.LicenceTemplatePage.replace(':id', id || ''),
          },
          t('licenceConfiguration.edit.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('licenceConfiguration.edit.header')}</StyledPageTitle>

      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
      {formValues && licence && !isLoading ? (
        <FormLicenceTemplatePage
          initialFormValues={formValues}
          isEdit={true}
          version={licence?.version}
        />
      ) : (
        <Loader isLoading={true} />
      )}
    </>
  );
};

export { EditLicenceTemplatePage };
